import { useEffect, useRef } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { Icon } from '@mypoz/ui';

import { useFormat } from '@/lib/format.js';
import { getCategoryLabelKey } from '@/features/notifications/catalog.js';
import type { DashboardNotification } from '@/features/notifications/types.js';

/**
 * The slide-over that opens when a notification has nowhere to link to, showing its own reference
 * in full — every notification's fallback until MYP-67, and still what a category with no landing
 * page opens (see getNotificationDestination), plus an ORDER notification with no `orderno` to
 * link from (MYP-112).
 *
 * Since MYP-67, a PACKAGE notification only reaches this if its subject can't be resolved to a
 * real package (see resolveNotificationLink) — normally it now links straight to that package's
 * own thread instead, `PackageMessagesSheet`, which is where post_packages_msg replying actually
 * lives. This panel stays read-only: it has no package to reply against once it's showing here.
 *
 * There is no message prose to show here at all as of MYP-112: `get_msgs` sends no `content`
 * field on the real wire, only a reference number and a date, so that's what this renders — the
 * same information `NotificationRow` already showed before the row was opened, just larger.
 *
 * Fetching real content is deliberately not done from here, even where a source exists. PACKAGE
 * already gets its own real thread via `resolveNotificationLink` instead of ever reaching this
 * panel with content to show. ORDER *does* have a source — an order's own `order_list` detail
 * row carries a `msglist` array shaped just like `get_packages_msg`'s rows, confirmed live for
 * MYP-112 — but there is no thread UI anywhere in the Orders feature to show it in, and building
 * one is a bigger change than this ticket's own Touches list scopes; ORDER links away instead
 * (see `resolveNotificationLink`) and only falls back to this panel when it has no `orderno` to
 * link from. AB/ALIPAY/AUTH/COMPEN have no detail endpoint at all — nothing to fetch, full stop.
 * This panel stays the generic "nowhere else to go" fallback, not a second content-fetching path
 * alongside `PackageMessagesSheet`.
 */
export function NotificationThreadPanel({
  notification,
  onClose,
}: {
  notification: DashboardNotification;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const closeRef = useRef<HTMLButtonElement>(null);

  const labelKey = getCategoryLabelKey(notification.type);
  const categoryLabel = labelKey ? t(labelKey) : notification.type;
  const title = t('notifications.thread.title', { reference: notification.reference });

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={onClose}
        aria-label={t('actions.close')}
        className="absolute inset-0 cursor-pointer bg-black/45 backdrop-blur-[1px]"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="absolute inset-y-0 right-0 flex w-[min(94vw,30rem)] flex-col bg-surface shadow-raised"
      >
        <header className="flex items-center gap-3 border-b border-line px-5 py-4">
          <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-ink">{title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            className="flex size-11 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-alt hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:size-9"
          >
            <Icon name="close" size={22} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <article className="rounded-lg bg-surface-alt px-4 py-3">
            <p className="text-xs text-muted">
              {categoryLabel}
              {notification.msgdate ? ` · ${fmt.dateTime(notification.msgdate)}` : null}
            </p>
            <p className="mt-1 text-sm text-ink">{notification.reference}</p>
          </article>
        </div>
      </aside>
    </div>
  );
}
